import "dayjs/locale/de";
import "dayjs/locale/en";

import {
  Add,
  AssessmentOutlined,
  Email,
  FileDownloadOutlined,
  Inventory,
  Language,
  ListAltRounded,
  Lock,
  Logout,
  NotificationsOutlined,
  PublishedWithChanges,
  SentimentSatisfiedAlt,
  SettingsOutlined,
  SpaceDashboardOutlined,
} from "@mui/icons-material";
import {
  Notification,
  NotificationChannel,
  NotificationsPlugin,
  NotificationsPluginState,
  ScheduledNotification,
} from "@pimo/notifications-plugin-v2";
import {
  App,
  composeInitialState,
  LoginPlugin,
  LoginPluginPartialAppState,
  UniversalAppState,
} from "@pimo/pimo-app-builder";
import {
  DefaultOverlay,
  DefaultOverlayNotificationPayload,
  DefaultOverlayPartialAppState,
  DragDropLayoutState,
  DragDropLayoutTabDefinition,
  initialDefaultOverlayState,
  Log,
  PMWelcomeScreen,
  TableSortData,
} from "@pimo/pimo-components";
import {
  getLocalizedMessage,
  L10nPlugin,
  type SupportedLanguage,
} from "@pimo/pimo-l10n";
import {
  type LockDetail,
  ResourceLockPlugin,
} from "@pimo/resource-lock-plugin";
import { getDefaultStepsForRole } from "@pimo/tutorial";
import dayjs from "dayjs";
import {
  BasicItem,
  BffDashboard,
  BffOverlay,
  BffOverview,
  BffReport,
  ColumnDefinition,
  Configuration,
  ExtendedFieldError,
  MfaPolicy,
  Profile,
  Project,
  ReportingItem,
} from "product-test-types";
import {
  canUserPerformOperation,
  determineUserRole,
  validateConfiguration,
} from "product-test-utils";
import React from "react";
import { generatePath, matchPath } from "react-router-dom";

import { APP_KEY } from "../app-key";
import { FilterData } from "./components/project-filter-dialog";
import { TrialExpiredModal } from "./components/trial-expired-modal";
import { UpdatesView } from "./components/updates-view/updates-view";
import { APP_ROUTES, RESOURCE_LOCK_POLLING_INTERVAL } from "./constants";
import { HOME_PORTAL_LINK, STRAPI_URL, THEME_NAME } from "./env";
import { fetchDashboard } from "./helpers/fetch-dashboard";
import { fetchEntityConfiguration } from "./helpers/fetch-entity-configurations";
import { downloadExcelExport } from "./helpers/fetch-excel-export";
import { fetchOverlay } from "./helpers/fetch-overlay";
import { fetchOverview } from "./helpers/fetch-overview";
import {
  deleteAllProjects,
  fetchAllProjects,
  fetchProject,
} from "./helpers/fetch-project";
import {
  fetchSettings,
  fetchSetupSteps,
  fetchUsers,
  SeatInfo,
} from "./helpers/fetch-settings";
import { fetchTableConfiguration } from "./helpers/fetch-table-configuration";
import {
  fetchUserSettings,
  saveUserSettings,
  UserSettings,
} from "./helpers/fetch-user-settings";
import {
  fetchViewConfiguration,
  mapViewConfiguration,
} from "./helpers/fetch-view-configuration";
import { generateDebugMagicLink } from "./helpers/generate-debug-magic-link";
import { getTutorialEntityNames } from "./helpers/get-tutorial-entity-names";
import { patchLockDetails } from "./helpers/patch-lock-details";
import { showSnackbar } from "./helpers/show-snackbar";
import de from "./locales/de.json";
import en from "./locales/en.json";
import { DashboardPlugin } from "./pages/dashboard-plugin";
import { HomePlugin } from "./pages/home-plugin";
import { LogsPlugin } from "./pages/logs-plugin";
import { MagicLinkPlugin } from "./pages/magic-link-plugin";
import { NoAccessPlugin } from "./pages/no-access-plugin";
import { NotFoundPlugin } from "./pages/not-found-plugin";
import { OverviewPlugin } from "./pages/overview-plugin";
import { ReportPlugin } from "./pages/report-plugin";
import {
  buildUsersLoadStatePatch,
  SettingsPlugin,
} from "./pages/settings-plugin";
import { theme } from "./theme";
import { getAddProjectButtonLabel } from "./utils/get-add-new-label";
import { getLockedByMessage } from "./utils/get-locked-by-message";
import {
  getItemName,
  getPluralOfItemName,
} from "./utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "./utils/get-plural-of-project-name";
import { getTotalAndCompletedSteps } from "./utils/get-total-and-completed-steps";
import { isTrialExpired } from "./utils/is-trial-expired";

const THEME_LOGOS: Record<string, string> = {
  allianz: "allianz.svg",
  default: "pimo-large.svg",
};

const appLogo = THEME_LOGOS[THEME_NAME] ?? "pimo-large.svg";

export type TestAppState = DefaultOverlayPartialAppState &
  LoginPluginPartialAppState<Profile> &
  UniversalAppState &
  NotificationsPluginState &
  DefaultOverlayPartialAppState & {
    number1: number;
    number2: number;
    editFormLoading?: boolean;
    dashboardLayout?: DragDropLayoutState;
    overviewLayout?: DragDropLayoutState;
    reportLayout?: DragDropLayoutState;
    overlay?: BffOverlay;
    currentReport?: BffReport;
    showMaturitySubmissionDialog?: boolean;
    showNoDeliverablesDialog?: boolean;
    dashboard?: BffDashboard;
    overview?: BffOverview & {};
    projects?: Project[];
    settings?: {
      projects?: Project[];
      basicItems?: BasicItem[];
      configuration?: Configuration;
      mfaPolicy?: MfaPolicy;
    };
    editSettingsForm?: {
      configuration?: Configuration;
      basicItems?: BasicItem[];
      projects?: Project[];
      validationErrors?: Record<string, ExtendedFieldError>;
      deletedProjects?: number[];
      deletedBasicItems?: number[];
      users?: Profile[];
      enableSaveButton?: boolean;
      saveInProgress?: boolean;
      hasUpdatedUsers?: boolean;
      clientUsers?: Profile[];
      toolUsers?: Profile[];
      seatInfo?: SeatInfo | null;
      mfaPolicy?: MfaPolicy;
      hasCreatedFields?: boolean;
      hasDeletedFields?: boolean;
    };
    editForm?: {
      currentTrackingItem?: BasicItem;
      currentProject?: Project;
    };
    changedNotifications?: Notification[];
    changedScheduledNotifications?: ScheduledNotification[];
    //
    logs?: Log[];
    uniqueEditors?: string[];
    logsSearch?: {
      fromDate?: string | null;
      untilDate?: string | null;
      selectedProject?: string;
      selectedEditor?: string;
      searchTerm?: string;
      projects?: Array<{ id: string; name: string }>;
      uniqueEditors?: Array<{ id: string; name: string }>;
    };
    activeMenu?: string | null;
    magicLinkFailed?: boolean;
    isLoading: boolean;
    isSidebarOpen: boolean;
    currentviewname?: string;
    dashboardComponentValues?: Record<string, unknown>;
    reportComponentValues?: Record<string, unknown>;
    language: SupportedLanguage;
    reportPluginSortData: TableSortData<BasicItem & ReportingItem>;
    overviewPluginSortData: TableSortData<Project>;
    reportPluginColumnOrder?: number[];
    overviewPluginColumnOrder?: number[];
    /** Filters of the projects overview; kept while navigating to a report and back. */
    filterData?: FilterData;
    /** Filters of the report's items table; reset whenever a report is loaded. */
    reportFilterData?: FilterData;
    importDialog?: {
      excelHeaders: string[];
      itemFields: string[];
      open: boolean;
      file: File | null;
      suggestedMapping: Record<string, string>;
      isLoading: boolean;
      excelData: unknown[][];
    };
    reportTableConfiguration?: {
      isEditMode: boolean;
      tableHeaderEntries: ColumnDefinition[];
      confirmationPopupType?: "save" | "abort";
      isDirty?: boolean;
    };
    overviewTableConfiguration?: {
      isEditMode: boolean;
      tableHeaderEntries: ColumnDefinition[];
      confirmationPopupType?: "save" | "abort";
      isDirty?: boolean;
    };
    // Confirmation dialog flags for delete actions
    showDeleteProjectDialog?: boolean;
    pendingDeleteProjectId?: string | number | undefined;
    showDeleteTrackingItemDialog?: boolean;
    pendingDeleteTrackingItemId?: string | number | undefined;
    // Confirmation dialog for saving report layout changes
    showConfirmSaveReportLayoutDialog?: boolean;
    showClearAllDataDialog?: boolean;
    showUnsavedSettingsDialog?: boolean;
    showModifiedFieldsDialog?: boolean;
    pendingSaveReportLayout?: {
      tabs: DragDropLayoutTabDefinition[];
      displayTabs: boolean;
      type?: "overview" | "dashboard" | "project-common";
    };
    // Confirmation dialog for saving overview layout changes
    showConfirmSaveOverviewLayoutDialog?: boolean;
    pendingSaveOverviewLayout?: {
      tabs: DragDropLayoutTabDefinition[];
      displayTabs: boolean;
      type?: "overview" | "dashboard" | "project-common";
    };
    // Tracking items details modal
    showTrackingItemDetailsModal?: boolean;
    selectedTrackingItem?: ReportingItem;
    showWelcomeScreen?: boolean;
    welcomeScreenStatus?: {
      steps?: {
        hasGeneralData: boolean;
        hasReportingData: boolean;
        hasProjectsAndDeliverables: boolean;
        hasInvitedUsers: boolean;
        hasNotifications: boolean;
      };
      totalSteps: number;
      completedSteps: number;
    };
    userSettings: UserSettings;
    welcomeScreenCompletedItems?: {
      hasGeneralData: boolean;
      hasReportingData: boolean;
      hasProjectsAndDeliverables: boolean;
      hasInvitedUsers: boolean;
      hasNotifications: boolean;
    };
    showEditFormConfirmationDialog?: boolean;
    showIncompleteDimensionAssessmentDialog?: boolean;
    recentlyCreatedProjectId?: string | null;
    showTrialExpiredDialog?: boolean;
    lockDetails?: {
      settingsLock?: LockDetail;
      projectLocks?: Record<
        string,
        { edit: Partial<LockDetail>; update: Partial<LockDetail> }
      >;
      itemLocks?: Record<string, Partial<LockDetail>>;
    };
  };

type DefaultOverlayTutorialConfig = React.ComponentProps<
  typeof DefaultOverlay
>["tutorial"];

/**
 * Builds the overlay tutorial config only when the current user should
 * actually see the tutorial, keeping the overlay mapState logic compact.
 */
function createTutorialConfig({
  app,
  language,
  userProfile,
  tool,
  projects,
  welcomeScreenStatus,
  userSettings,
  projectName,
  itemName,
}: {
  app: App<TestAppState>;
  language: SupportedLanguage;
  userProfile?: Profile;
  tool?: Configuration["tool"];
  projects?: Project[];
  welcomeScreenStatus?: TestAppState["welcomeScreenStatus"];
  userSettings?: UserSettings;
  projectName?: Configuration["projectName"];
  itemName?: Configuration["itemName"];
}): DefaultOverlayTutorialConfig | undefined {
  const userRole = determineUserRole(userProfile, tool);
  const entityNames = getTutorialEntityNames(projectName, itemName);
  const tutorialSteps = userRole
    ? getDefaultStepsForRole(
        userRole,
        language,
        entityNames,
        projects?.[0]
          ? [{ replaceToken: "{id}", value: projects[0]?.id }]
          : undefined
      )
    : [];

  const welcomeStepsCompleted =
    welcomeScreenStatus &&
    welcomeScreenStatus.completedSteps === welcomeScreenStatus.totalSteps;

  const shouldShowTutorial =
    userSettings != null &&
    (!welcomeStepsCompleted || userSettings.setupSkipped) &&
    !userSettings.tutorialCompleted;

  if (!shouldShowTutorial) {
    return undefined;
  }

  return {
    welcomeText:
      getLocalizedMessage(language, "tutorial.welcomeText") ||
      "Welcome to Pimo. Your reporting tool is now ready. Would you like to complete the tutorial first or finish setting up reporting right away?",
    welcomeLogo: "pimo-large.svg",
    steps: tutorialSteps,
    showTutorial: true,
    setTutorialCompleted: () => {
      const currentAppState = app.getAppState();
      const currentWelcomeScreenStatus = currentAppState.welcomeScreenStatus;

      const shouldShowWelcomeScreen =
        currentWelcomeScreenStatus &&
        currentWelcomeScreenStatus.totalSteps !==
          currentWelcomeScreenStatus.completedSteps &&
        !userSettings?.setupSkipped;

      app.patchAppState({
        showWelcomeScreen: shouldShowWelcomeScreen,
        userSettings: {
          ...app.getAppState().userSettings,
          tutorialCompleted: true,
        },
      });
      saveUserSettings({ tutorialCompleted: true });
    },
  };
}

// Plugins
const loginPlugin = new LoginPlugin<Profile, TestAppState>(
  APP_ROUTES.dashboard,
  STRAPI_URL,
  "product-test",
  {
    whitelistUrls: [APP_ROUTES.magicLink, APP_ROUTES.noAccess],
    redirectToBackendLoginIfLoggedOut: true,
    automaticallyDetectEndedSessions: true,
    automaticallyRefreshUserProfile: true,
  }
);
const magicLinkPlugin = new MagicLinkPlugin(() => loginPlugin.refreshSession());

loginPlugin.on("login-status-changed", async (evt) => {
  if (!evt.payload?.isLoggedIn) {
    return;
  }
  const user = app.getAppState().userProfile;

  // The configuration carries the language the app was created with in the
  // Home Portal. It is fetched first so every following request and the whole
  // UI use the resolved language right away.
  const config = await fetchEntityConfiguration();
  const language = l10nPlugin.applyDefaultLanguage(
    app,
    config?.defaultLanguage
  );

  const [
    settings,
    completedSetupSteps,
    userSettings,
    allNotifications,
    scheduledNotifications,
    settingsLockDetail,
  ] = await Promise.all([
    fetchSettings(language),
    fetchSetupSteps(),
    fetchUserSettings(),
    notificationsPlugin.fetchAllNotifications(),
    notificationsPlugin.fetchScheduledNotifications(),
    resourceLockPlugin.isLocked("settings"),
  ]);

  const trialExpired = await isTrialExpired(config?.homePortalAppId);

  const isOnWelcomeScreenRoute = !window.location.pathname.includes("settings");

  const totalSteps = Object.entries(completedSetupSteps || {}).length;
  const completedSteps = Object.values(completedSetupSteps || {}).reduce(
    (acc, curr) => {
      if (curr) {
        acc++;
      }

      return acc;
    },
    0
  );

  const shouldShowWelcomeScreen =
    isOnWelcomeScreenRoute &&
    totalSteps !== completedSteps &&
    !userSettings?.setupSkipped &&
    userSettings?.tutorialCompleted;

  app.patchAppState({
    settings: {
      projects: settings.projects,
      basicItems: settings.basicItems ?? [],
      configuration: settings.configuration || config,
      mfaPolicy: settings.mfaPolicy,
    },
    editSettingsForm: {
      projects: settings.projects,
      basicItems: settings.basicItems ?? [],
      configuration: settings.configuration || config,
      // fetchSettings() never returns toolUsers/clientUsers (only the
      // dedicated /bff-settings/users call does) - preserve whatever the
      // settings route loader already put there instead of wiping it with
      // undefined, which used to race and silently drop every existing
      // tool user on the next save.
      clientUsers: app.getAppState().editSettingsForm?.clientUsers,
      toolUsers: app.getAppState().editSettingsForm?.toolUsers,
      seatInfo: settings.seatInfo,
      mfaPolicy: settings.mfaPolicy,
    },
    showWelcomeScreen: shouldShowWelcomeScreen,
    welcomeScreenStatus: {
      steps: completedSetupSteps,
      totalSteps,
      completedSteps,
    },
    userSettings: userSettings ?? {
      tutorialCompleted: true,
      setupSkipped: true,
    },
    lockDetails: {
      ...app.getAppState().lockDetails,
      settingsLock: settingsLockDetail,
    },
    allNotifications: allNotifications,
    scheduledNotifications: scheduledNotifications,
    showTrialExpiredDialog: trialExpired ?? false,
  });

  if (
    canUserPerformOperation({
      userGroups: user?.groups || [],
      operation: "read",
      lowestLevelAccessRequiredToPerformThisOperation: "program",
      client: config?.client,
      tool: config?.tool,
    })
  ) {
    if (!shouldShowWelcomeScreen) {
      app.navigate(
        window.location.pathname === "/"
          ? APP_ROUTES.dashboard
          : window.location.pathname
      );
    }
  } else if (!determineUserRole(user, config?.tool)) {
    // Removed from all of this tool's role groups: there is no project to land on.
    app.navigate(APP_ROUTES.noAccess);
  } else {
    if (!shouldShowWelcomeScreen) {
      const projects = await fetchAllProjects();
      app.navigate(
        generatePath(APP_ROUTES.report, {
          id: projects?.[0].id ?? "",
          viewname: "dashboard",
        })
      );
    }
  }
});
const homePlugin = new HomePlugin<TestAppState>();
const dashboardPlugin = new DashboardPlugin();
const reportPlugin = new ReportPlugin();
const overviewPlugin = new OverviewPlugin();
const settingsPlugin = new SettingsPlugin();
const logsPlugin = new LogsPlugin();
// ProductTest owns the one-time lifecycle: backend publication and Banner/Email
// delivery options, with automatic bell membership.
const notificationsPlugin = new NotificationsPlugin<TestAppState>(
  STRAPI_URL,
  false,
  true,
  false,
  30_000,
  true
);
const resourceLockPlugin = new ResourceLockPlugin<TestAppState>(STRAPI_URL);

// Register locales from JSON files
const app = App.create<TestAppState>(
  composeInitialState<TestAppState>({
    ...initialDefaultOverlayState,
    ...loginPlugin.getInitialState(),
    isLoading: false,
    isSidebarOpen: true,
    number1: 1,
    number2: 2,
    reportPluginSortData: { key: "customId", direction: "ASC" },
    overviewPluginSortData: { key: "customId", direction: "ASC" },
    experimentalFeatures: {
      componentStateHydration: true,
    },
    language: "en" as SupportedLanguage,
    showWelcomeScreen: false,
    userSettings: {
      tutorialCompleted: true,
      setupSkipped: true,
    },
  }),
  APP_KEY
);
app.setTheme(theme);

app.on("init", () => {
  setInterval(async () => {
    const appState = app.getAppState();

    const [settingsLock, lockedProjects] = await Promise.all([
      resourceLockPlugin.isLocked("settings"),
      resourceLockPlugin.getLockedProjects(
        (appState.projects ?? []).map(({ id }) => id)
      ),
    ]);
    const lockedItems = await resourceLockPlugin.getLockedItems(
      appState.projects?.reduce((acc, project) => {
        const reportingItems = project.reportingItems ?? [];
        const itemIds = reportingItems.map((item) => `item-${item.id}`);
        return [...acc, ...itemIds];
      }, [] as string[]) ?? []
    );

    patchLockDetails(app, {
      settingsLock,
      projectLocks: lockedProjects,
      itemLocks: lockedItems,
    });
  }, RESOURCE_LOCK_POLLING_INTERVAL);
});

// Register enhanced L10nPlugin with locales and language detection
const l10nPlugin = new L10nPlugin<TestAppState>({
  supportedLanguages: ["en", "de"],
  defaultLanguage: "en",
  // detectBrowserLanguage: true, // Disabled - default language is English
  detectBrowserLanguage: false,
  storageKey: "language",
  locales: {
    en,
    de,
  },
});
app.registerPlugin(l10nPlugin);

const syncDayjsLocale = (language?: string) => {
  dayjs.locale((language || "en").toLowerCase().startsWith("de") ? "de" : "en");
};
syncDayjsLocale(app.getAppState().language);
app.on("state:changed", () => {
  syncDayjsLocale(app.getAppState().language);
});

// Overlay
const overlayView = app.createOverlayView({ name: "Overlay" });

const trialExpiredModal = overlayView.addComponent({
  component: TrialExpiredModal,
});

trialExpiredModal.mapState(({ showTrialExpiredDialog, language }) => ({
  open: showTrialExpiredDialog ?? false,
  language: language || "en",
}));

const pageOverlay = overlayView.addComponent({
  component: DefaultOverlay,
});

pageOverlay.on("overlay:show-logs", () => {
  app.navigate(APP_ROUTES.logs);
});

pageOverlay.on("overlay:notification-read", (params) => {
  const notificationsPlugin = app.getPluginByName<
    NotificationsPlugin<TestAppState>
  >("NotificationsPlugin");
  void notificationsPlugin?.handleOverlayNotificationReadEvent(
    params as { payload: DefaultOverlayNotificationPayload }
  );
});

pageOverlay.mapState(
  ({
    isSidebarOpen,
    userProfile,
    overlay,
    language,
    settings,
    snackbar,
    banner,
    welcomeScreenStatus,
    userSettings,
    projects,
    lockDetails,
  }) => {
    const settingsLockDetail = lockDetails?.settingsLock;
    const isLocked =
      settingsLockDetail?.isLocked &&
      settingsLockDetail.lockedBy !== userProfile?.email;
    const tutorial = createTutorialConfig({
      app,
      language,
      userProfile,
      tool: settings?.configuration?.tool,
      projects,
      welcomeScreenStatus,
      userSettings,
      projectName: settings?.configuration?.projectName,
      itemName: settings?.configuration?.itemName,
    });

    return {
      snackbar,
      buttonProps: canUserPerformOperation({
        userGroups: userProfile?.groups || [],
        operation: "edit",
        object: "project",
        lowestLevelAccessRequiredToPerformThisOperation: "program",
        client: settings?.configuration?.client,
        tool: settings?.configuration?.tool,
      })
        ? {
            size: "medium",
            variant: "white",
            icon: <Add />,
            tourid: "new-project",
            text: getAddProjectButtonLabel(language),
            onClick: () => {
              app?.navigate(
                generatePath(APP_ROUTES.projectDialog, {
                  id: "new-project",
                  isFromOverview: "true",
                })
              );
            },
          }
        : undefined,
      enableLogs: true,
      logs: [],

      menuEntries: [
        ...(canUserPerformOperation({
          userGroups: userProfile?.groups || [],
          operation: "read",
          lowestLevelAccessRequiredToPerformThisOperation: "program",
          client: settings?.configuration?.client,
          tool: settings?.configuration?.tool,
        })
          ? [
              {
                title:
                  getLocalizedMessage(language, "dashboard") ||
                  "Program Dashboard",
                tourid: "dashboard",
                link: `${APP_ROUTES.dashboard}`,
                icon: SpaceDashboardOutlined,
                level: 0,
                active: !!matchPath(
                  { path: APP_ROUTES.dashboard, end: true }, // ← exact match
                  window.location.pathname
                ),
              },
            ]
          : []),
        ...(canUserPerformOperation({
          userGroups: userProfile?.groups || [],
          operation: "read",
          lowestLevelAccessRequiredToPerformThisOperation: "program",
          client: settings?.configuration?.client,
          tool: settings?.configuration?.tool,
        })
          ? [
              {
                title: getLocalizedMessage(language, "overview", {
                  overviewItem: getPluralOfProjectName(
                    settings?.configuration?.projectName,
                    language
                  ),
                }),
                link: `${APP_ROUTES.overview}`,
                icon: ListAltRounded,
                level: 0,
              },
            ]
          : []),
        {
          title: getLocalizedMessage(language, "reports") || "Reports",
          tourid: "project-reports",
          level: 0,
          icon: AssessmentOutlined,
          items: overlay?.projects?.map((entry) => ({
            level: 1,
            link: generatePath(APP_ROUTES.report, {
              id: String(entry.id),
              viewname: "dashboard",
            }),
            active: window.location.pathname.includes(
              `reports/${entry.id ?? ""}/`
            ),
            title: `${entry.customId ? entry.customId + " - " : ""}${
              entry.name
            }`,
          })),
          // highlight the parent “Reports” heading whenever you’re on any report
          active: window.location.pathname.includes(`reports/`),
        },
      ],

      header: {
        logo: {
          big: settings?.configuration?.logo ?? appLogo,
          small: settings?.configuration?.logo ?? appLogo,
          minWidth: 100,
          maxWidth: 130,
        },
        username: userProfile?.email,
        fullName: userProfile?.name,
        actionButtons: [
          createNotificationsButton(app.getAppState(), app),
          createUpdatesButton(app.getAppState(), app),

          ...(canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            operation: "read",
            lowestLevelAccessRequiredToPerformThisOperation: "program",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          })
            ? [
                {
                  icon: PublishedWithChanges,
                  onClick: () => {
                    pageOverlay.fireEvent("overlay:show-logs");
                  },
                },
              ]
            : []),

          ...(canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          })
            ? [
                {
                  icon: isLocked ? Lock : SettingsOutlined,
                  disabled: isLocked,
                  onClick: () => {
                    resourceLockPlugin.lock("settings");
                    app?.navigate(APP_ROUTES.settings.reportingStructure);
                    app.patchAppState({ isSidebarOpen: false });
                  },
                  tooltip: getLockedByMessage(
                    "settingsPage",
                    language,
                    settingsLockDetail
                  ),
                  text:
                    getLocalizedMessage(language, "appSettings") ||
                    "Reporting Settings",
                },
              ]
            : []),

          ...(canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          })
            ? [
                {
                  icon: FileDownloadOutlined,
                  menu: {
                    items: [
                      {
                        label:
                          getLocalizedMessage(
                            language || "en",
                            "download.excel"
                          ) || "Download Excel",
                        onClick: () => downloadExcelExport(),
                      },
                    ],
                  },
                },
              ]
            : []),
        ],
        entries: [
          {
            text:
              getLocalizedMessage(
                language || "en",
                "userMenu.contactPimoTeam"
              ) || "Contact Pimo team",
            onClick: () => {
              window.open("mailto:contact@pimoapp.com");
            },
            icon: Email,
          },
          {
            text: `${
              getLocalizedMessage(language, "userMenu.language") || "Language"
            }: ${getLocalizedMessage(
              language || "en",
              `settingsPage.${language === "en" ? "english" : "german"}`
            )}`,
            menu: {
              onChange: (value) => {
                app.patchAppState({ language: value as SupportedLanguage });
                if (
                  matchPath("/settings/:viewname", window.location.pathname)
                ) {
                  void settingsPlugin.refreshSettingsForLanguage(
                    value as SupportedLanguage
                  );
                }
              },
              items: [
                {
                  label:
                    getLocalizedMessage(
                      language || "en",
                      "settingsPage.german"
                    ) ?? "German",
                  value: "de",
                },
                {
                  label:
                    getLocalizedMessage(
                      language || "en",
                      "settingsPage.english"
                    ) ?? "English",
                  value: "en",
                },
              ],
            },
            icon: Language,
          },
          ...(import.meta.env.MODE !== "production" &&
          canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          })
            ? [
                {
                  text: "Generate magic link (debug)",
                  onClick: async () => {
                    const defaultEmail = userProfile?.email ?? "";
                    const userEmail = window
                      .prompt("User email", defaultEmail)
                      ?.trim();
                    if (!userEmail) {
                      return;
                    }

                    try {
                      const result = await generateDebugMagicLink({
                        userEmail,
                      });
                      await navigator.clipboard.writeText(result.url);
                      showSnackbar(
                        app as unknown as App<
                          UniversalAppState & DefaultOverlayPartialAppState
                        >,
                        `Magic link copied (expires ${result.expiresAt})`,
                        "success"
                      );
                    } catch (error) {
                      console.error(
                        "Magic link generation failed in product test:",
                        error
                      );
                      showSnackbar(
                        app as unknown as App<
                          UniversalAppState & DefaultOverlayPartialAppState
                        >,
                        "Failed to generate magic link",
                        "error"
                      );
                    }
                  },
                  icon: Add,
                },
              ]
            : []),
          {
            text:
              getLocalizedMessage(
                language || "en",
                "userMenu.homeportalLink"
              ) || "To Home Portal",
            onClick: () => {
              window.open(HOME_PORTAL_LINK);
            },
            icon: Inventory,
          },
          {
            text: getLocalizedMessage(language, "logout") || "Logout",
            onClick: () => {
              loginPlugin.logout();
            },
            icon: Logout,
          },
        ],
      },
      isSidebarOpen,
      notifications: {
        banner,
      },
      ...(welcomeScreenStatus &&
      welcomeScreenStatus.totalSteps != 0 &&
      welcomeScreenStatus.totalSteps !== welcomeScreenStatus.completedSteps
        ? {
            setupSteps: {
              totalSteps: welcomeScreenStatus?.totalSteps ?? 0,
              completedSteps: welcomeScreenStatus?.completedSteps ?? 0,
            },
          }
        : {}),
      language: language,
      ...(tutorial ? { tutorial } : {}),
    };
  }
);
const createUpdatesView = (state: TestAppState) => ({
  id: "updates",
  content: React.createElement(UpdatesView, {
    projects: state?.projects || [],
    reportingDate:
      state.settings?.configuration?.reportingCycle?.endReportingCycleDate ||
      "",
  }),
});

const createUpdatesButton = (state: TestAppState, app: App<TestAppState>) => {
  return {
    icon: SentimentSatisfiedAlt,
    badgeValue: state?.projects?.length || 0,
    onClick: () => app.patchAppState({ activeMenu: "updates" }),
    actionSidebar: {
      items: [createUpdatesView(state)],
      isOpen: state.activeMenu === "updates",
      onClose: () => app.patchAppState({ activeMenu: null }),
      title:
        getLocalizedMessage(state.language || "en", "updates.title") ||
        "Reporting Updates",
    },
  };
};

const getBellNotifications = (state: TestAppState): Notification[] =>
  state.allNotificationsForUser?.filter(
    (notification) =>
      (notification.channels?.includes(NotificationChannel.NOTIFICATION) ||
        notification.channels?.includes(NotificationChannel.BANNER)) &&
      notification.active &&
      notification.published
  ) ?? [];

const createNotificationsButton = (
  state: TestAppState,
  app: App<TestAppState>
) => ({
  icon: NotificationsOutlined,
  badgeValue: getBellNotifications(state).length,
  onClick: () => app.patchAppState({ activeMenu: "notifications" }),
  actionSidebar: {
    items: generateNotificationItems(state, app),
    isOpen: state.activeMenu === "notifications",
    onClose: () => app.patchAppState({ activeMenu: null }),
    title:
      getLocalizedMessage(
        state.language || "en",
        "setupSteps.notificationsTitle"
      ) || "Notifications",
  },
});

const generateNotificationItems = (
  state: TestAppState,
  app: App<TestAppState>
) => {
  const notifications = getBellNotifications(state);

  if (notifications.length === 0) return [];

  return notifications.map((notification) => ({
    id: notification.id ?? 0,
    notification: {
      id: notification.id ?? 0,
      title: notification.title,
      text: notification.text,
      createdAt: notification.createdAt,
      isRead: notification.isRead,
    },
    onClick: () => {
      const notificationsPlugin = app.getPluginByName<
        NotificationsPlugin<TestAppState>
      >("NotificationsPlugin");
      if (notificationsPlugin && notification.id) {
        void notificationsPlugin.handleOverlayNotificationReadEvent({
          payload: { notification },
        });
      }
    },
  }));
};

pageOverlay.on("overlay:toggle-sidebar", () => {
  app.patchAppState({
    isSidebarOpen: !app.getAppState().isSidebarOpen,
  });
});

pageOverlay.on("overlay:open-setup-screen", () => {
  app.patchAppState({ showWelcomeScreen: true });
});

const welcomeScreenComponent = overlayView.addComponent({
  component: PMWelcomeScreen,
});

welcomeScreenComponent.mapState(
  ({
    showWelcomeScreen,
    welcomeScreenStatus,
    language,
    lockDetails,
    userProfile,
    settings,
  }) => {
    return {
      isOpen: showWelcomeScreen ?? false,
      completedItems: welcomeScreenStatus?.steps ?? {
        hasGeneralData: false,
        hasReportingData: false,
        hasProjectsAndDeliverables: false,
        hasInvitedUsers: false,
        hasNotifications: false,
      },
      language: language || "en",
      settingsLocked:
        (lockDetails?.settingsLock?.isLocked &&
          lockDetails?.settingsLock?.lockedBy !== userProfile?.email) ??
        false,
      lockedMessage: getLockedByMessage(
        "settingsPage",
        language,
        lockDetails?.settingsLock
      ),
      projectName: {
        single:
          getProjectName(settings?.configuration?.projectName, language) ||
          "Project",
        plural: getPluralOfProjectName(
          settings?.configuration?.projectName,
          language
        ),
      },
      itemName: {
        single:
          getItemName(settings?.configuration?.itemName, language) ||
          "Tracking Item",
        plural: getPluralOfItemName(
          settings?.configuration?.itemName,
          language
        ),
      },
      hasProjects: Boolean(settings?.projects && settings.projects.length > 0),
      deleteSampleProjects: async () => {
        if (
          canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            operation: "edit",
            object: "project",
            lowestLevelAccessRequiredToPerformThisOperation: "program",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          })
        ) {
          try {
            await deleteAllProjects();

            showSnackbar(
              app as unknown as App<
                UniversalAppState & DefaultOverlayPartialAppState
              >,
              getLocalizedMessage(
                language || "en",
                "manageProjects.deleteAllSuccess"
              ) || "All Projects were deleted successfully",
              "success"
            );
            setTimeout(
              () => app.navigate(APP_ROUTES.settings.manageMainEntities),
              500
            );
          } catch (error) {
            console.error("Error deleting projects:", error);
            showSnackbar(
              app as unknown as App<
                UniversalAppState & DefaultOverlayPartialAppState
              >,
              getLocalizedMessage(language, "generic.internalError") ||
                "An internal error occurred, please try again.",
              "error"
            );
          }
        }
      },
    };
  }
);

welcomeScreenComponent.on(
  "welcome-screen:navigate",
  ({ payload: destination }) => {
    app.patchAppState({
      showWelcomeScreen: false,
    });

    if (destination === "settings-general-data") {
      app.navigate(APP_ROUTES.settings.reportingStructure);
    } else if (destination === "settings-reporting-data") {
      app.navigate(APP_ROUTES.settings.manageItemsDataModel);
    } else if (destination === "settings-project-content") {
      app.navigate(APP_ROUTES.settings.manageMainEntities);
    } else if (destination === "settings-invite-user") {
      app.navigate(APP_ROUTES.settings.manageUsers);
    } else if (destination === "settings-notifications") {
      app.navigate(APP_ROUTES.settings.notifications);
    }
  }
);

welcomeScreenComponent.on("welcome-screen:close", () => {
  saveUserSettings({ setupSkipped: true });
  app.patchAppState({
    showWelcomeScreen: false,
    userSettings: {
      ...app.getAppState().userSettings,
      setupSkipped: true,
    },
  });
});

app.registerPlugin(loginPlugin);
app.registerPlugin(magicLinkPlugin);
app.registerPlugin(homePlugin);
app.registerPlugin(dashboardPlugin);
app.registerPlugin(overviewPlugin);
app.registerPlugin(reportPlugin);
app.registerPlugin(notificationsPlugin);
app.registerPlugin(settingsPlugin);
app.registerPlugin(logsPlugin);
app.registerPlugin(resourceLockPlugin);
app.registerPlugin(new NoAccessPlugin(() => loginPlugin.logout()));
app.registerPlugin(new NotFoundPlugin());

magicLinkPlugin.route?.on("load", async () => {
  await magicLinkPlugin.redeemCurrentLink();
});

overviewPlugin.route?.on("load", async () => {
  const [overview, overlay, viewConfiguration, projects, tableConfiguration] =
    await Promise.all([
      fetchOverview(),
      fetchOverlay(),
      fetchViewConfiguration("overview"),
      fetchAllProjects(),
      fetchTableConfiguration("projectsTableComponent"),
    ]);

  const lockedProjects = await resourceLockPlugin.getLockedProjects(
    (overview?.projects ?? []).map((project) => `project-${project.id}`)
  );

  app.patchAppState({
    ...(overview
      ? {
          overview,
        }
      : {}),
    overlay,
    projects,
    editForm: {},
    lockDetails: {
      ...app.getAppState().lockDetails,
      projectLocks: lockedProjects,
    },
    overviewTableConfiguration: {
      isEditMode: false,
      tableHeaderEntries: tableConfiguration?.columns ?? [],
      confirmationPopupType: undefined,
    },
    ...(viewConfiguration
      ? {
          overviewLayout: mapViewConfiguration(viewConfiguration),
        }
      : {}),
  });
});

dashboardPlugin.route?.on("load", async () => {
  const [
    dashboard,
    overlay,
    viewConfiguration,
    configuration,
    projects,
    settings,
    completedSetupSteps,
    settingsLockDetail,
  ] = await Promise.all([
    fetchDashboard(),
    fetchOverlay(),
    fetchViewConfiguration("dashboard"),
    fetchEntityConfiguration(),
    fetchAllProjects(),
    fetchSettings(),
    fetchSetupSteps(),
    resourceLockPlugin.isLocked("settings"),
  ]);

  const { totalSteps, completedSteps } =
    getTotalAndCompletedSteps(completedSetupSteps);

  app.patchAppState({
    isSidebarOpen: true,
    dashboard,
    filterData: undefined,
    overlay,
    projects,
    settings: { ...settings, configuration: configuration },
    editSettingsForm: {
      ...settings,
      configuration: configuration,
    },
    lockDetails: {
      ...app.getAppState().lockDetails,
      settingsLock: settingsLockDetail,
    },
    welcomeScreenStatus: {
      steps: completedSetupSteps,
      totalSteps,
      completedSteps,
    },
    ...(viewConfiguration
      ? {
          dashboardLayout: mapViewConfiguration(viewConfiguration),
        }
      : {}),
  });
});

reportPlugin.route?.on("load", async ({ payload }) => {
  const params = payload?.parameters;
  if (!params?.id) {
    return;
  }

  const [
    currentReport,
    overlay,
    viewConfiguration,
    projects,
    tableConfiguration,
  ] = await Promise.all([
    fetchProject({ id: params.id }),
    fetchOverlay(),
    fetchViewConfiguration("project-common"),
    fetchAllProjects(),
    fetchTableConfiguration("trackingItemsTableComponent"),
  ]);

  const [lockedItems, lockedProjects] = await Promise.all([
    resourceLockPlugin.getLockedItems(
      (currentReport?.project?.reportingItems ?? []).map(
        (item) => `item-${item.id}`
      )
    ),
    resourceLockPlugin.getLockedProjects((projects ?? []).map(({ id }) => id)),
  ]);

  if (!app) {
    return;
  }
  const appState = app.getAppState();

  let userEmail = appState.userProfile?.email;
  // In some cases, when opening the report page directly, the user profile might not be immediately available.
  // We wait for a short time and check again to ensure we have the user's email before determining the lock status.
  if (!userEmail) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    userEmail = appState.userProfile?.email;
  }

  app.patchAppState({
    currentReport,
    lockDetails: {
      ...appState.lockDetails,
      itemLocks: lockedItems,
      projectLocks: lockedProjects,
    },
    overlay,
    reportFilterData: undefined,
    projects,
    ...(viewConfiguration
      ? {
          reportLayout: mapViewConfiguration(viewConfiguration),
        }
      : {}),
    reportTableConfiguration: {
      isEditMode: false,
      tableHeaderEntries: tableConfiguration?.columns ?? [],
      confirmationPopupType: undefined,
      isDirty: false,
    },
  });
});

settingsPlugin.route?.on("load", async () => {
  app.patchAppState({
    isLoading: true,
  });
  const language = app.getAppState().language || "en";
  const [settings, completedSetupSteps, users] = await Promise.all([
    fetchSettings(language),
    fetchSetupSteps(),
    fetchUsers(),
  ]);
  if (!app) {
    return;
  }
  if (users === undefined) {
    // Fetching the roster failed (network/server error, including a
    // Keycloak MFA-detail lookup failure). Tell the admin instead of
    // silently rendering an empty user list they might then publish.
    showSnackbar(
      app as unknown as App<UniversalAppState & DefaultOverlayPartialAppState>,
      getLocalizedMessage(language, "generic.internalError") ||
        "An internal error occurred, please try again.",
      "error"
    );
  }
  const totalSteps = Object.entries(completedSetupSteps || {}).length;
  const completedSteps = Object.values(completedSetupSteps || {}).reduce(
    (acc, curr) => {
      if (curr) {
        acc++;
      }

      return acc;
    },
    0
  );

  app.patchAppState({
    isLoading: false,
    filterData: undefined,
    editSettingsForm: {
      projects: settings.projects,
      basicItems: settings.basicItems ?? [],
      configuration: settings.configuration,
      seatInfo: settings.seatInfo,
      mfaPolicy: settings.mfaPolicy,

      ...buildUsersLoadStatePatch(users),

      validationErrors: validateConfiguration(
        settings.configuration,
        settings.projects ?? [],
        settings.basicItems ?? [],
        settings.toolUsers ?? [],
        settings.clientUsers ?? [],
        {
          fieldRequired:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
          dateNotValid:
            getLocalizedMessage(
              language || "en",
              "validationErrors.dateNotValid"
            ) || "This date is not valid",
          endDateAfterStartDate:
            getLocalizedMessage(
              language || "en",
              "validationErrors.endDateAfterStartDate"
            ) || "End date should be after start date",
        }
      ),
      enableSaveButton: false,
    },
    settings: settings,
    welcomeScreenStatus: {
      steps: completedSetupSteps,
      totalSteps,
      completedSteps,
    },
  });
});
const RenderedApp = app.render();

export default RenderedApp;
